import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { RefreshCw, Save, ShieldAlert } from "lucide-react";
import {
  DEFAULT_RESET_CLASSIFICATIONS,
  FAULT_CLASSIFICATION_OPTIONS,
  RESET_SETTING_NAME,
  parseResetClassifications,
  resetSettingRow,
} from "@/lib/incidentResponsibility";

type InsuranceExcessSettingsForm = {
  insurance_excess_current_target: string;
  insurance_excess_step_6mo: string;
  insurance_excess_step_12mo: string;
  insurance_excess_step_18mo_full: string;
  insurance_excess_no_claim_bonus_amount: string;
  insurance_excess_tenure_window_months: string;
  insurance_excess_min_months_before_claim: string;
};

const DEFAULTS: InsuranceExcessSettingsForm = {
  insurance_excess_current_target: "2500",
  insurance_excess_step_6mo: "800",
  insurance_excess_step_12mo: "1600",
  insurance_excess_step_18mo_full: "2500",
  insurance_excess_no_claim_bonus_amount: "300",
  insurance_excess_tenure_window_months: "18",
  insurance_excess_min_months_before_claim: "6",
};

const SETTING_NAMES = Object.keys(DEFAULTS) as (keyof InsuranceExcessSettingsForm)[];

const InsuranceExcessSettings: React.FC = () => {
  const [form, setForm] = useState<InsuranceExcessSettingsForm>(DEFAULTS);
  // Weekly contribution isn't editable here at all -- it always mirrors
  // Payment Settings' "Insurance Excess", the number actually deducted from
  // driver pay. Shown read-only so it's still visible in context.
  const [weeklyContribution, setWeeklyContribution] = useState<string>("15");
  // Which fault classifications restart a driver's excess cycle when their
  // responsibility for an incident is confirmed (see confirm_incident_excess).
  const [resetKeys, setResetKeys] = useState<string[]>(DEFAULT_RESET_CLASSIFICATIONS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("settings")
      .select("name, value")
      .in("name", [...SETTING_NAMES, "insurance_excess", RESET_SETTING_NAME]);
    setLoading(false);

    if (error) {
      toast({ title: "Insurance Excess Settings error", description: error.message, variant: "destructive" });
      return;
    }

    const mapped = { ...DEFAULTS };
    (data || []).forEach((row) => {
      if (row.name in mapped) mapped[row.name as keyof InsuranceExcessSettingsForm] = row.value || "";
      if (row.name === "insurance_excess") setWeeklyContribution(row.value || "15");
      if (row.name === RESET_SETTING_NAME) setResetKeys(parseResetClassifications(row.value ?? ""));
    });
    setForm(mapped);
  };

  useEffect(() => {
    load();
  }, []);

  const updateField = (field: keyof InsuranceExcessSettingsForm, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const save = async () => {
    setSaving(true);

    const rows = [
      {
        name: "insurance_excess_current_target",
        value: form.insurance_excess_current_target,
        category: "InsuranceExcess",
        label: "Current Full Excess Amount ($)",
        description: "The full insurance excess as it currently stands on the policy -- reviewed at each renewal, so kept as a setting rather than hardcoded.",
        type: "number",
        sort_order: 10,
        is_active: true,
      },
      {
        name: "insurance_excess_step_6mo",
        value: form.insurance_excess_step_6mo,
        category: "InsuranceExcess",
        label: "Claimable After Minimum Tenure ($)",
        description: "What an at-fault driver can be charged once they've passed the minimum months before a claim.",
        type: "number",
        sort_order: 20,
        is_active: true,
      },
      {
        name: "insurance_excess_step_12mo",
        value: form.insurance_excess_step_12mo,
        category: "InsuranceExcess",
        label: "Claimable at Midpoint Tenure ($)",
        description: "What an at-fault driver can be charged once they've passed the midpoint between the minimum-tenure and full-tenure windows.",
        type: "number",
        sort_order: 30,
        is_active: true,
      },
      {
        name: "insurance_excess_step_18mo_full",
        value: form.insurance_excess_step_18mo_full,
        category: "InsuranceExcess",
        label: "Claimable at Full Tenure ($)",
        description: "The full excess amount an at-fault driver can be charged once they've reached the full tenure window.",
        type: "number",
        sort_order: 40,
        is_active: true,
      },
      {
        name: "insurance_excess_no_claim_bonus_amount",
        value: form.insurance_excess_no_claim_bonus_amount,
        category: "InsuranceExcess",
        label: "No Claim Rebate ($)",
        description: "Refunded to a contributor who reaches the full tenure window with no at-fault claims.",
        type: "number",
        sort_order: 60,
        is_active: true,
      },
      {
        name: "insurance_excess_tenure_window_months",
        value: form.insurance_excess_tenure_window_months,
        category: "InsuranceExcess",
        label: "Full Tenure Window (months)",
        description: "How long a driver must contribute with no at-fault claims to reach the full excess amount and become eligible for the No Claim Rebate.",
        type: "number",
        sort_order: 70,
        is_active: true,
      },
      {
        name: "insurance_excess_min_months_before_claim",
        value: form.insurance_excess_min_months_before_claim,
        category: "InsuranceExcess",
        label: "Minimum Tenure Before Any Claim (months)",
        description: "No claim can be made against the fund until a driver has been contributing for at least this long.",
        type: "number",
        sort_order: 80,
        is_active: true,
      },
      resetSettingRow(resetKeys),
    ];

    const { error } = await supabase.from("settings").upsert(rows, { onConflict: "name" });
    setSaving(false);

    if (error) {
      toast({ title: "Not saved", description: error.message, variant: "destructive" });
      return;
    }

    toast({ title: "Saved", description: "Insurance Excess settings updated." });
  };

  const renderNumberSetting = (field: keyof InsuranceExcessSettingsForm, label: string, description: string, suffix: string) => (
    <div className="space-y-3 rounded-lg border border-border bg-background/40 p-4">
      <div>
        <Label className="text-foreground">{label}</Label>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={0}
          value={form[field]}
          onChange={(e) => updateField(field, e.target.value)}
          className="dark-input"
        />
        <span className="text-xs text-muted-foreground shrink-0">{suffix}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-yellow-500" />
            Insurance Excess Settings
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            The driver excess fund's graduated tiers and weekly contribution -- these fluctuate with each policy
            renewal, so they're fully configurable here rather than hardcoded.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {renderNumberSetting(
          "insurance_excess_current_target",
          "Current Full Excess Amount",
          "The full insurance excess as it currently stands on the policy.",
          "$"
        )}
        <div className="space-y-3 rounded-lg border border-border bg-background/40 p-4">
          <div>
            <Label className="text-foreground">Weekly Contribution</Label>
            <p className="text-xs text-muted-foreground mt-1">
              Always matches Payment Settings → Insurance Excess, the amount actually deducted from driver pay each week.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="dark-input flex-1 rounded-md px-3 py-2 text-sm opacity-80">${weeklyContribution}</div>
            <span className="text-xs text-muted-foreground shrink-0">$ / week</span>
          </div>
        </div>
        {renderNumberSetting(
          "insurance_excess_min_months_before_claim",
          "Minimum Tenure Before Any Claim",
          "No claim can be made against the fund until a driver has contributed for at least this long.",
          "months"
        )}
        {renderNumberSetting(
          "insurance_excess_step_6mo",
          "Claimable After Minimum Tenure",
          "What an at-fault driver can be charged once past the minimum tenure.",
          "$"
        )}
        {renderNumberSetting(
          "insurance_excess_step_12mo",
          "Claimable at Midpoint Tenure",
          "What an at-fault driver can be charged once past the midpoint tenure.",
          "$"
        )}
        {renderNumberSetting(
          "insurance_excess_tenure_window_months",
          "Full Tenure Window",
          "How long with no at-fault claims to reach the full excess amount and No Claim Rebate eligibility. The rebate is due again this long after each one is paid.",
          "months"
        )}
        {renderNumberSetting(
          "insurance_excess_step_18mo_full",
          "Claimable at Full Tenure",
          "The full excess amount an at-fault driver can be charged once at full tenure.",
          "$"
        )}
        {renderNumberSetting(
          "insurance_excess_no_claim_bonus_amount",
          "No Claim Rebate",
          "Refunded to a contributor who reaches full tenure with no at-fault claims.",
          "$"
        )}
      </div>

      <div className="space-y-3 rounded-lg border border-border bg-background/40 p-4">
        <div>
          <Label className="text-foreground">Claims That Restart a Driver's Excess Cycle</Label>
          <p className="text-xs text-muted-foreground mt-1">
            When responsibility for an incident is confirmed, a driver's insurance tenure and repayment cycle restarts
            only if the incident's fault classification is ticked here. It starts as <strong>At fault</strong> only.
            Every claim, ticked or not, stays in the driver's claim history -- repeated incidents matter for
            suitability and risk either way.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {FAULT_CLASSIFICATION_OPTIONS.map((o) => (
            <label key={o.key} className="flex items-start gap-2 text-sm">
              <Checkbox
                className="mt-0.5"
                checked={resetKeys.includes(o.key)}
                onCheckedChange={(v) =>
                  setResetKeys((prev) => (v ? [...new Set([...prev, o.key])] : prev.filter((k) => k !== o.key)))
                }
              />
              <span>
                {o.label}
                <span className="block text-xs text-muted-foreground">{o.hint}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="w-full golden-button">
        <Save className="w-4 h-4 mr-2" />
        {saving ? "Saving Insurance Excess Settings..." : "Save Insurance Excess Settings"}
      </Button>
    </div>
  );
};

export default InsuranceExcessSettings;
